/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Phone as PhoneIcon,
  MessageCircle,
  ThumbsUp,
  AlertTriangle,
  Stethoscope,
  Wrench,
  Newspaper,
  Milk,
  Carrot,
  Building2,
  Tag,
} from "lucide-react";
import StarRating from "./StarRating";
import { directoryApi, formatPhone, telLink, waLink } from "./api";
import "./DirectoryPage.css";

const CATEGORY_ICONS = {
  emergency: AlertTriangle,
  medical: Stethoscope,
  "home-services": Wrench,
  newspapers: Newspaper,
  "milk-vendor": Milk,
  "vegetable-vendor": Carrot,
  "facility-manager": Building2,
};

function categoryIcon(slug) {
  return CATEGORY_ICONS[slug] || Tag;
}

function categoryColor(slug) {
  switch (slug) {
    case "emergency":
      return "vsd-cat-emergency";
    case "medical":
      return "vsd-cat-medical";
    case "home-services":
      return "vsd-cat-services";
    case "newspapers":
      return "vsd-cat-newspaper";
    case "milk-vendor":
      return "vsd-cat-milk";
    case "vegetable-vendor":
      return "vsd-cat-vegetable";
    case "facility-manager":
      return "vsd-cat-facility";
    default:
      return "vsd-cat-other";
  }
}

export default function DirectoryPage() {
  const [categories, setCategories] = useState([]);
  const [contactsByCat, setContactsByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [picksOnly, setPicksOnly] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Always fetch the full set; the category sidebar filters client-side
      // so it stays stable while switching.
      const data = await directoryApi.listContacts({
        q: debouncedSearch || undefined,
      });
      setCategories(data.categories || []);
      setContactsByCat(data.contacts_by_category || {});
    } catch (e) {
      setError(e.message || "Could not load directory");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const totalCount = useMemo(
    () =>
      Object.values(contactsByCat).reduce((n, arr) => n + arr.length, 0),
    [contactsByCat]
  );

  // Only categories that actually have contacts (hide empty ones).
  const nonEmptyCategories = useMemo(
    () => categories.filter((c) => (contactsByCat[c.id] || []).length > 0),
    [categories, contactsByCat]
  );

  // If the active category becomes empty (e.g. via search), fall back to All.
  useEffect(() => {
    if (
      activeTab !== "all" &&
      !nonEmptyCategories.some((c) => c.slug === activeTab)
    ) {
      setActiveTab("all");
    }
  }, [nonEmptyCategories, activeTab]);

  const shownCategories =
    activeTab === "all"
      ? nonEmptyCategories
      : nonEmptyCategories.filter((c) => c.slug === activeTab);

  return (
    <div className="vsd-shell">
      <div className="vsd-head">
        <div>
          <h1>Directory</h1>
          <p className="vsd-sub">Community-curated service contacts</p>
        </div>
        <button
          className="vsd-add-btn"
          onClick={() => navigate("/directory/new")}
        >
          <Plus size={16} /> Add contact
        </button>
      </div>

      <div className="vsd-layout">
        {/* Category sidebar (non-empty categories only) */}
        <aside className="vsd-sidebar">
          <button
            className={`vsd-side-cat ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            <span className="vsd-side-cat-icon vsd-cat-other">
              <Tag size={15} strokeWidth={2.1} />
            </span>
            <span className="vsd-side-cat-name">All contacts</span>
            {totalCount > 0 && (
              <span className="vsd-side-cat-count">{totalCount}</span>
            )}
          </button>
          {nonEmptyCategories.map((c) => {
            const Icon = categoryIcon(c.slug);
            const n = (contactsByCat[c.id] || []).length;
            return (
              <button
                key={c.id}
                className={`vsd-side-cat ${activeTab === c.slug ? "active" : ""}`}
                onClick={() => setActiveTab(c.slug)}
              >
                <span className={`vsd-side-cat-icon ${categoryColor(c.slug)}`}>
                  <Icon size={15} strokeWidth={2.1} />
                </span>
                <span className="vsd-side-cat-name">{c.name}</span>
                <span className="vsd-side-cat-count">{n}</span>
              </button>
            );
          })}
        </aside>

        {/* Main content */}
        <div className="vsd-main">
          <div className="vsd-search-row">
            <div className="vsd-search">
              <Search size={16} />
              <input
                type="search"
                placeholder="Search contacts by name, service…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              className={`vsd-picks-btn ${picksOnly ? "active" : ""}`}
              onClick={() => setPicksOnly((v) => !v)}
              title="Show community-recommended contacts only"
            >
              ⭐ Picks
            </button>
          </div>

          {error && <div className="vsd-error">{error}</div>}
          {loading && totalCount === 0 && (
            <div className="vsd-empty">Loading…</div>
          )}

          {!loading &&
            !error &&
            shownCategories.map((cat) => {
              const Icon = categoryIcon(cat.slug);
              return (
                <div key={cat.id} className="vsd-section">
                  <h2 className={`vsd-section-h ${categoryColor(cat.slug)}`}>
                    <span className="vsd-section-h-icon">
                      <Icon size={16} strokeWidth={2.2} />
                    </span>
                    {cat.name}
                    <span className="vsd-section-h-count">
                      {(contactsByCat[cat.id] || []).length}
                    </span>
                  </h2>

                  <div className="vsd-tiles">
                    {(contactsByCat[cat.id] || [])
                      .filter((c) => !picksOnly || c.is_community_pick)
                      .map((c) => (
                        <ContactCard key={c.id} contact={c} />
                      ))}
                  </div>
                </div>
              );
            })}

          {!loading && !error && totalCount === 0 && (
            <div className="vsd-empty">
              {debouncedSearch
                ? "No contacts match your search."
                : "No contacts in the directory yet. Be the first to add one!"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactCard({ contact }) {
  const navigate = useNavigate();
  const open = () => navigate(`/directory/contact/${contact.id}`);
  return (
    <div
      className="vsd-tile"
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      <div className="vsd-avatar">
        {contact.avatar_url ? (
          <img src={contact.avatar_url} alt={contact.name} />
        ) : (
          <span>{(contact.name || "?").charAt(0).toUpperCase()}</span>
        )}
      </div>

      <div className="vsd-tile-main">
        <div className="vsd-card-name">
          {contact.name}
          {contact.is_community_pick && (
            <span className="vsd-pick" title="Community Pick">
              <ThumbsUp size={11} strokeWidth={2.5} /> Pick
            </span>
          )}
        </div>
        <div className="vsd-tile-sub">
          <span className="vsd-card-cat">{contact.category?.name || ""}</span>
          {contact.review_count > 0 ? (
            <StarRating
              value={contact.rating}
              count={contact.review_count}
              showCount
              readOnly
              size={13}
            />
          ) : (
            <span className="vsd-card-no-reviews">No reviews yet</span>
          )}
        </div>
      </div>

      <div className="vsd-tile-phone">
        <span className="vsd-meta-phone">
          {formatPhone(contact.primary_phone)}
        </span>
        {contact.working_hours && (
          <span className="vsd-meta-hours">{contact.working_hours}</span>
        )}
      </div>

      <div className="vsd-tile-actions" onClick={(e) => e.preventDefault()}>
        <a
          href={telLink(contact.primary_phone)}
          className="vsd-action vsd-action-call"
          onClick={(e) => e.stopPropagation()}
          aria-label="Call"
          title="Call"
        >
          <PhoneIcon size={16} strokeWidth={2.1} />
        </a>
        <a
          href={waLink(contact.whatsapp_phone || contact.primary_phone)}
          target="_blank"
          rel="noreferrer"
          className="vsd-action vsd-action-wa"
          onClick={(e) => e.stopPropagation()}
          aria-label="WhatsApp"
          title="WhatsApp"
        >
          <MessageCircle size={16} strokeWidth={2.1} />
        </a>
      </div>
    </div>
  );
}
